/**
 * useMemories — serverdata for assistentens hukommelse (AI-21, #193).
 *
 * Kontraktene er `@stell/shared` (`memories.schemas.ts`, #191); rutene er
 * `apps/api/src/http/ai.memories.routes.ts`:
 *   - useMemories()          GET    /api/ai/memories            synlige + pending + own_pending + enabled
 *   - useDeleteMemory()      DELETE /api/ai/memories/:id        Angre i chatten og Slett
 *   - useWithdrawMemory()    POST   /api/ai/memories/:id/withdraw  Trekk tilbake (#218)
 *   - useUpdateMemory()      PATCH  /api/ai/memories/:id        tekst / målgruppe
 *   - useResolveMemory()     POST   /api/ai/memories/:id/approve|reject  (admin)
 *   - useMemorySettings()    GET    /api/ai/memories/settings   (admin)
 *   - useSetMemoryEnabled()  PUT    /api/ai/memories/settings   (admin, fersk scope)
 *
 * Serveren håndhever synlighet og skrivepolicy (Pattern 5); klienten bare
 * skjuler knapper. Hver skriving invaliderer lista, så brikken i chatten,
 * innstillingssiden og bryteren alltid leser det serveren sier (invariant 2).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import type {
  MemoryDeletedResponse,
  MemoryListResponse,
  MemorySettings,
  MemoryDTO,
  UpdateMemoryRequest,
} from '@stell/shared';
import { ApiError, jsonInit, sendJson } from '../../lib/api';

export const MEMORIES_KEY = ['ai', 'memories'] as const;
export const MEMORY_SETTINGS_KEY = ['ai', 'memories', 'settings'] as const;
/** Brikken i chatten leser Angre-status herfra med `useMutationState` (MemoryChip). */
export const DELETE_MEMORY_KEY = ['ai', 'memories', 'delete'] as const;

const BASE = '/api/ai/memories';

export function useMemories(enabled = true) {
  return useQuery<MemoryListResponse>({
    queryKey: MEMORIES_KEY,
    enabled,
    queryFn: () => sendJson<MemoryListResponse>(BASE),
  });
}

function useInvalidateMemories() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: MEMORIES_KEY });
}

/**
 * Sletting og tilbaketrekking (#218) deler svar og regel: `not_found` regnes
 * som gjort — notatet er borte, og det var det brukeren ba om.
 */
async function removeNote(url: string, method: 'DELETE' | 'POST'): Promise<void> {
  try {
    await sendJson<MemoryDeletedResponse>(url, { method });
  } catch (err) {
    if (err instanceof ApiError && err.code === 'not_found') return;
    throw err;
  }
}

/**
 * Sletter et notat. `not_found` regnes som slettet: et notat som alt er borte
 * (slettet i innstillingene, eller Angre trykket to ganger) er nettopp det
 * brukeren ba om.
 */
export function useDeleteMemory() {
  const invalidate = useInvalidateMemories();
  return useMutation({
    mutationKey: DELETE_MEMORY_KEY,
    // Brikken leser Angre-status fra mutasjonsbufferen (MemoryChip). Med
    // standard gcTime (5 min) forsvant den, og en angret brikke viste «Husket»
    // og Angre igjen (review-runde 1 i !175). Slettingene er få og små; de
    // lever like lenge som økten.
    gcTime: Infinity,
    mutationFn: (id: string) => removeNote(`${BASE}/${id}`, 'DELETE'),
    onSettled: invalidate,
  });
}

/**
 * Trekker tilbake et eget ventende forslag (#218). Serveren sletter bare hvis
 * det fortsatt venter; har en forelder avgjort det i mellomtiden, er svaret
 * 409 `not_pending` og notatet står (`withdrawErrorText`). `not_found` regnes
 * som trukket tilbake, som i `useDeleteMemory`. Lista hentes på nytt uansett
 * utfall, så skjermen viser det forelderen bestemte.
 */
export function useWithdrawMemory() {
  const invalidate = useInvalidateMemories();
  return useMutation({
    mutationFn: (id: string) => removeNote(`${BASE}/${id}/withdraw`, 'POST'),
    onSettled: invalidate,
  });
}

export function useUpdateMemory() {
  const invalidate = useInvalidateMemories();
  return useMutation({
    mutationFn: (args: { id: string; patch: UpdateMemoryRequest }) =>
      sendJson<{ memory: MemoryDTO }>(`${BASE}/${args.id}`, jsonInit('PATCH', args.patch)),
    onSettled: invalidate,
  });
}

export function useResolveMemory() {
  const invalidate = useInvalidateMemories();
  return useMutation({
    mutationFn: (args: { id: string; decision: 'approve' | 'reject' }) =>
      sendJson<{ memory: MemoryDTO }>(`${BASE}/${args.id}/${args.decision}`, { method: 'POST' }),
    onSettled: invalidate,
  });
}

export function useMemorySettings(enabled = true) {
  return useQuery<MemorySettings>({
    queryKey: MEMORY_SETTINGS_KEY,
    enabled,
    queryFn: () => sendJson<MemorySettings>(`${BASE}/settings`),
  });
}

export function useSetMemoryEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      sendJson<MemorySettings>(`${BASE}/settings`, jsonInit('PUT', { enabled })),
    onSuccess: (data) => {
      qc.setQueryData(MEMORY_SETTINGS_KEY, data);
    },
    // Lista bærer også `enabled` (linjen «Hukommelsen er slått av»).
    onSettled: () => qc.invalidateQueries({ queryKey: MEMORIES_KEY }),
  });
}

/**
 * En feil fra hukommelses-API-et som en norsk setning. `memory_refused` bærer
 * vaktens årsak (`MemoryGuardReasonSchema`; `allergen`/`diet` peker til
 * kostholdsinnstillingene, Pattern 6); ellers er feilkoden fra memory.service
 * nøkkelen. Nøklene i `assistant.memory.errors` er lista over kjente koder —
 * en kode uten tekst får den generiske.
 */
export function memoryErrorText(t: TFunction, err: unknown): string {
  const generic = t('assistant.memory.errors.generic');
  if (!(err instanceof ApiError)) return generic;
  const code = err.code === 'memory_refused' ? err.reason : err.code;
  return code ? t(`assistant.memory.errors.${code}`, { defaultValue: generic }) : generic;
}

/**
 * Feilen fra «Trekk tilbake» (#218). 409 `not_pending` betyr at en forelder
 * rakk å godkjenne eller avvise forslaget først — det sier vi rett ut, siden
 * raden samtidig flytter seg eller forsvinner når lista hentes på nytt.
 * Andre feil går som `memoryErrorText`.
 */
export function withdrawErrorText(t: TFunction, err: unknown): string {
  if (err instanceof ApiError && err.code === 'not_pending') return t('assistant.memory.own_pending.withdraw_not_pending');
  return memoryErrorText(t, err);
}
